import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Pencil, Share2, ShieldCheck } from "lucide-react";
import { JobSearchStatus } from "@api/shared-runtime/enums";
import type { ProviderUser } from "@/common/types/ProviderUser";
import { Button } from "@/components/ui/button";
import type { Credential } from "@/features/credentials/types";
import { ProfileImage } from "@/features/profile/components/ProfileImage";
import { SharingProfileModal } from "@/features/profile/components/SharingProfileModal";
import { getProfileIdentity } from "@/features/profile/components/profile-overview/profile-identity-data";
import { cn } from "@/lib/utils";
import { useTRPC, type RouterOutputs } from "@/trpc";
import { ProfileStateLicenses } from "./profile-state-licenses";

type ProfileHeroProps = {
  user: ProviderUser;
  credentials: Credential[];
  jobPreferences: RouterOutputs["profile"]["getJobPreferences"] | undefined;
  onOpenCredentials: () => void;
  onEditProfile: () => void;
};

function SearchStatusBadge({
  status,
}: {
  status: JobSearchStatus | undefined;
}) {
  let label: string;
  if (status === JobSearchStatus.ActivelyLooking) {
    label = "Actively looking";
  } else if (status === JobSearchStatus.PassivelyLooking) {
    label = "Open to opportunities";
  } else if (status === JobSearchStatus.NotLooking) {
    label = "Not currently looking";
  } else {
    return null;
  }

  const isLooking = status !== JobSearchStatus.NotLooking;

  return (
    <span
      className={cn(
        "inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold",
        isLooking
          ? "border-emerald-100 bg-emerald-50 text-emerald-700"
          : "border-slate-200 bg-slate-50 text-slate-500",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "size-1.5 rounded-full",
          isLooking ? "bg-emerald-500" : "bg-slate-400",
        )}
      />
      {label}
    </span>
  );
}

export function ProfileHero({
  user,
  credentials,
  jobPreferences,
  onOpenCredentials,
  onEditProfile,
}: ProfileHeroProps) {
  const trpc = useTRPC();
  const [sharing, setSharing] = useState(false);
  const identity = getProfileIdentity(credentials);
  const specialtiesQuery = useQuery(
    trpc.healthcareProfessionalTypes.getByCodes.queryOptions(
      { codes: identity.specialtyCodes },
      { enabled: identity.specialtyCodes.length > 0 },
    ),
  );
  const fullName = [
    identity.firstName,
    identity.middleName,
    identity.lastName,
    identity.suffixName,
  ]
    .filter(Boolean)
    .join(" ");
  const specialtyDetails = [
    ...new Set([
      ...(specialtiesQuery.data?.map((specialty) => specialty.name) ?? []),
      ...identity.specialties,
    ]),
  ]
    .filter(Boolean)
    .join(" · ");
  const location = [identity.address.city, identity.address.state]
    .filter(Boolean)
    .join(", ");
  let specialtySummary = specialtyDetails;
  if (specialtiesQuery.isLoading && !specialtyDetails) {
    specialtySummary = "Loading specialties…";
  } else if (specialtiesQuery.isError) {
    specialtySummary = specialtyDetails || "Specialties unavailable";
  }

  return (
    <header className="relative isolate overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 xl:p-8">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-12 -z-10 size-80 rounded-full bg-primary/5 blur-3xl"
      />
      <div className="flex flex-col justify-between gap-7 xl:flex-row xl:items-start">
        <div className="flex min-w-0 flex-col items-start gap-5 sm:flex-row sm:items-center sm:gap-6">
          <div className="shrink-0 rounded-2xl shadow-sm ring-1 ring-primary/10">
            <ProfileImage
              editable
              userId={user.id}
              profileImagePath={user.profileImagePath}
              firstName={identity.firstName}
              lastName={identity.lastName}
              avatarClassName="size-20 rounded-2xl sm:size-24 [&>span]:rounded-2xl [&>span]:bg-primary [&>img]:object-cover"
            />
          </div>
          <div className="min-w-0 space-y-2.5">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl [overflow-wrap:anywhere]">
                {fullName || "Your profile"}
              </h1>
              {user.jobPostsEnabled && (
                <SearchStatusBadge status={jobPreferences?.searchStatusId} />
              )}
            </div>
            <div className="space-y-1">
              <p className="text-base font-medium text-slate-700 sm:text-lg">
                {identity.professionalType || "Add your professional details"}
              </p>
              {specialtySummary && (
                <p className="text-sm leading-6 text-slate-500">
                  {specialtySummary}
                </p>
              )}
            </div>
            <ProfileStateLicenses credentials={credentials} />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-500">
              {identity.npiNumber && (
                <span className="tracking-wide">NPI: {identity.npiNumber}</span>
              )}
              {location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin aria-hidden="true" className="size-3.5" />
                  {location}
                </span>
              )}
              <Button
                variant="ghost"
                onClick={onEditProfile}
                className="gap-2 px-3 text-sm font-semibold text-primary hover:bg-primary/5 hover:text-primary"
              >
                <Pencil aria-hidden="true" className="size-4" />
                Edit profile
              </Button>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-3 xl:flex-col xl:items-stretch 2xl:flex-row">
          <Button
            onClick={() => setSharing(true)}
            className="flex-1 gap-2 px-5 text-sm font-semibold sm:flex-none"
          >
            <Share2 aria-hidden="true" className="size-4" />
            Share profile
          </Button>
          <Button
            variant="outline"
            onClick={onOpenCredentials}
            className="flex-1 gap-2 px-5 text-sm font-semibold text-slate-700 sm:flex-none"
          >
            <ShieldCheck aria-hidden="true" className="size-4 text-primary" />
            View credentials
          </Button>
        </div>
      </div>
      <SharingProfileModal
        appearance="profile"
        userId={user.id}
        open={sharing}
        onOpenChange={setSharing}
      />
    </header>
  );
}
